import { useState, useMemo } from "react";
import { InsightsNavbar } from "@/components/InsightsNavbar";
import { AnimatedSection } from "@/components/AnimatedSection";
import { Footer } from "@/components/Footer";
import { SEO, createFAQSchema } from "@/components/SEO";
import { glossaryTerms, GlossaryTerm } from "@/data/glossary";
import { Search, X, BookOpen, Shield, Mail, BarChart3, Bot, ShieldAlert } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const categoryInfo: Record<string, { label: string; icon: typeof Shield; color: string }> = {
  'email-auth': { label: 'Email Authentication', icon: Shield, color: 'text-emerald-500' },
  'deliverability': { label: 'Deliverability', icon: Mail, color: 'text-blue-500' },
  'security': { label: 'Security', icon: ShieldAlert, color: 'text-red-500' },
  'ai-development': { label: 'AI Development', icon: Bot, color: 'text-purple-500' },
  'metrics': { label: 'Metrics', icon: BarChart3, color: 'text-amber-500' },
  'productivity': { label: 'Productivity', icon: BarChart3, color: 'text-cyan-500' },
};

const Glossary = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string>("all");

  const filteredTerms = useMemo(() => {
    let result = glossaryTerms;
    
    if (activeCategory !== "all") {
      result = result.filter(t => t.category === activeCategory);
    }
    
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(t => 
        t.term.toLowerCase().includes(q) || 
        t.definition.toLowerCase().includes(q)
      );
    }
    
    return result.sort((a, b) => a.term.localeCompare(b.term));
  }, [searchQuery, activeCategory]);

  // Create FAQ schema for GEO - LLMs love definitions
  const faqSchema = createFAQSchema(
    glossaryTerms.slice(0, 15).map(t => ({
      question: `What is ${t.term}?`,
      answer: t.definition
    }))
  );

  const categories = ['all', ...Object.keys(categoryInfo)] as const;

  return (
    <>
      <SEO
        title="Email Deliverability Glossary"
        description="Comprehensive glossary of email deliverability terms including SPF, DKIM, DMARC, sender reputation, domain warmup, and more. Learn the essential terminology for email marketing success."
        canonical="/glossary"
        keywords={["email glossary", "SPF definition", "DKIM explained", "DMARC meaning", "email deliverability terms", "email marketing terminology"]}
        jsonLd={faqSchema}
      />
      <InsightsNavbar />
      <main className="min-h-screen">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted/30" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
          
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-20 sm:pt-24 pb-8 sm:pb-10">
            <AnimatedSection delay={0} className="text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-5">
                <BookOpen className="w-4 h-4" />
                Reference Guide
              </div>
              
              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-3">
                Email Deliverability Glossary
              </h1>
              <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
                Essential terminology for email authentication, deliverability, security, and AI development. 
                {glossaryTerms.length} terms explained.
              </p>
            </AnimatedSection>
          </div>
        </section>

        {/* Content Section */}
        <section className="relative pb-12 sm:pb-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            {/* Search */}
            <AnimatedSection delay={50} className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search terms..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-10 bg-card border-border"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </AnimatedSection>

            {/* Category Filter */}
            <AnimatedSection delay={100} className="mb-8">
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => {
                  const isActive = activeCategory === cat;
                  const info = cat === 'all' ? null : categoryInfo[cat as keyof typeof categoryInfo];
                  
                  return (
                    <Button
                      key={cat}
                      variant={isActive ? "default" : "outline"}
                      size="sm"
                      onClick={() => setActiveCategory(cat)}
                      className="rounded-full text-xs"
                    >
                      {info && <info.icon className="w-3 h-3 mr-1.5" />}
                      {cat === 'all' ? 'All' : info?.label}
                    </Button>
                  );
                })}
              </div>
            </AnimatedSection>

            {/* Terms List */}
            <AnimatedSection delay={150}>
              <div className="space-y-4">
                {filteredTerms.map((term) => {
                  const catInfo = categoryInfo[term.category];
                  return (
                    <article 
                      key={term.slug}
                      id={term.slug}
                      className="bg-card border border-border rounded-xl p-5 hover:border-primary/30 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <h2 className="font-display text-lg font-semibold text-foreground">
                          {term.term}
                        </h2>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-muted ${catInfo.color}`}>
                          <catInfo.icon className="w-3 h-3" />
                          {catInfo.label}
                        </span>
                      </div>
                      
                      <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                        {term.definition}
                      </p>
                      
                      {term.example && (
                        <div className="bg-muted/50 rounded-lg p-3 font-mono text-xs text-muted-foreground overflow-x-auto">
                          {term.example}
                        </div>
                      )}
                      
                      {term.relatedTerms && term.relatedTerms.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          <span className="text-xs text-muted-foreground">Related:</span>
                          {term.relatedTerms.map((related) => (
                            <span key={related} className="text-xs text-primary">
                              {related}
                            </span>
                          ))}
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>

              {filteredTerms.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  No terms found matching your search.
                </div>
              )}
            </AnimatedSection>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default Glossary;
